<template>

  <v-card>
    <v-card-title primary-title>
      {{ $t('assets.variable-manager') }}
    </v-card-title>

    <v-card-text>
      <v-container fluid class="my-0">
        <v-row class="px-4">
          <!-- device type selector -->
          <v-col class="px-2">
            <v-autocomplete clearable hide-no-data
              v-model="deviceType"
              item-text="name"
              item-value="id"
              :label="$t('devices.type')"
              :items="lazySelect[selectModel].listItems"
              :search-input.sync="lazySelect[selectModel].search"
              :loading="lazySelect[selectModel].loading"
              @focus="updateLazySelect({ model: selectModel })"
            />
          </v-col>
          <v-col v-if="managerDisplayed">
            <v-tabs v-model="tab" right grow>
              <v-tab v-for="name in tabs" :key="name" :disabled="tabDisabled(name)">{{ name }}</v-tab>
            </v-tabs>
          </v-col>
        </v-row>

        <!-- hints -->
        <v-row v-if="!deviceType">
          <v-col>
            <p class="text-center grey--text">
              <v-icon small color="grey">info</v-icon> {{ $t('variable-manager.help') }}
            </p>
          </v-col>
        </v-row>
        <v-row v-if="!deviceAssets.length && deviceType">
          <v-col>
            <p class="text-center grey--text">
              <v-icon small color="grey">info</v-icon> {{ $t('variable-manager.no-assets') }}
            </p>
          </v-col>
        </v-row>
        <v-row v-if="loading">
          <v-progress-circular indeterminate color="primary" class="ret-center-absolute"></v-progress-circular>
        </v-row>

        <v-row v-if="managerDisplayed">
          <!-- variables -->
          <v-col class="py-0" v-if="tab === 0">
            <v-list dense>
              <v-subheader>
                <v-checkbox v-model="allVariables" @change="toggleAllVariables" :title="$t('all')" />
                <span class="pb-1">{{ $tc('variables.entity-name', deviceVariables.length) }}</span>
              </v-subheader>
              <v-list-item v-for="name in deviceVariables" :key="name" class="px-2">
                <v-list-item-action>
                  <v-checkbox v-model="selectedVariables" :value="name" hide-details>
                     <template v-slot:label>
                       <span class="ml-2 black--text">{{ name }}</span>
                     </template>
                  </v-checkbox>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
          <!-- commands -->
          <v-col class="py-0" v-if="tab === 1">
            <v-list dense>
              <v-subheader>
                <v-checkbox v-model="allCommands" @change="toggleAllCommands" :title="$t('all')" />
                <span class="pb-1">{{ $tc('commands', deviceCommands.length) }}</span>
              </v-subheader>
              <v-list-item v-for="name in deviceCommands" :key="name" class="px-2">
                <v-list-item-action>
                  <v-checkbox v-model="selectedCommands" :value="name" hide-details>
                     <template v-slot:label>
                       <span class="ml-2 black--text">{{ name }}</span>
                     </template>
                  </v-checkbox>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
          <!-- assets -->
          <v-col class="py-0">
            <v-list dense>
              <v-subheader>
                <v-checkbox v-model="allAssets" @change="toggleAllAssets" :title="$t('all')" />
                <span class="pb-1">{{ $tc('assets.entity-name', deviceAssets.length) }}</span>
              </v-subheader>
              <v-list-item v-for="asset in deviceAssets" :key="asset.id" class="px-2">
                <v-list-item-content>
                  <v-list-item-title>
                    <v-checkbox v-model="selectedAssetIds" :value="asset.id" hide-details class="ma-0">
                      <template v-slot:label>
                        <span class="black--text">{{ asset.fullPath }}</span>
                      </template>
                    </v-checkbox>
                  </v-list-item-title>
                  <v-item-group class="pl-8" v-if="tab === 0 && selectedAssetIds.includes(asset.id)">
                    <!-- asset variables -->
                    <v-chip outlined label small class="mr-1 mb-1 pl-1 pr-2"
                      v-for="{ color, icon, name } in getVariableChips(asset.id)"
                      :key="name"
                      :color="color"
                    >
                      <v-icon small>{{ icon }}</v-icon> {{ name }}
                    </v-chip>
                  </v-item-group>
                  <!-- asset commands -->
                  <v-item-group class="pl-8" v-if="tab === 1 && selectedAssetIds.includes(asset.id)">
                    <v-chip outlined label small class="mr-1 mb-1 pl-1 pr-2"
                      v-for="{ color, icon, name } in getCommandChips(asset.id)"
                      :key="name"
                      :color="color"
                    >
                      <v-icon small>{{ icon }}</v-icon> {{ name }}
                    </v-chip>
                  </v-item-group>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions>
      <v-btn text @click="ok()">{{ $t('ok') }}</v-btn>
      <v-btn text @click="close()">{{ $t('cancel') }}</v-btn>
    </v-card-actions>
  </v-card>

</template>

<script>
import lazySelects from '../../helpers/lazy-selects.es6'

export default {
  name: 'asset-manager',
  mixins: [lazySelects],
  data () {
    return {
      deviceType: null,
      deviceAssets: [],
      selectedAssetIds: [],
      allAssets: false,
      deviceVariables: [],
      selectedVariables: [],
      allVariables: false,
      deviceCommands: [],
      selectedCommands: [],
      allCommands: false,
      selectModel: 'device-types',
      autocomplete: [
        { model: 'device-types', value: 'deviceType', fetchSelected: false }
      ],
      loading: false,
      saveLoading: false,
      saveVariablesDisabled: true,
      saveCommandsDisabled: true,
      tabs: ['Variables', 'Commands'],
      tab: null
    }
  },
  computed: {
    managerDisplayed () {
      return this.deviceType && this.deviceVariables.length && this.deviceAssets.length
    }
  },
  watch: {
    async deviceType (deviceType) {
      // reset selections
      this.selectedAssetIds = []
      this.selectedVariables = []
      this.selectedCommands = []
      this.saveVariablesDisabled = true
      this.saveCommandsDisabled = true

      if (!deviceType) {
        return
      }

      const { lazySelect, selectModel } = this
      const { listItems } = lazySelect[selectModel]
      // load device variables
      this.deviceVariables = listItems.reduce((variables, item) => {
        if (item.name === deviceType) {
          variables = Object.keys(item.variables)
        }
        return variables
      }, [])
      // load device commands
      this.deviceCommands = listItems.reduce((commands, item) => {
        if (item.name === deviceType) {
          commands = item.commands.map((command) => command.method)
        }
        return commands
      }, [])
      // load associated assets
      this.loading = true
      this.deviceAssets = await this.getAssets(deviceType)
      this.loading = false
    },
    selectedVariables (next, prev) {
      this.allVariables = this.selectedVariables.length === this.deviceVariables.length
      if (next.length !== prev.length && this.selectedAssetIds.length > 0) {
        this.saveVariablesDisabled = false
      }
    },
    selectedCommands (next, prev) {
      this.allCommands = this.selectedCommands.length === this.deviceCommands.length
      if (next.length !== prev.length && this.selectedAssetIds.length > 0) {
        this.saveCommandsDisabled = false
      }
    },
    selectedAssetIds (next, prev) {
      this.allAssets = this.selectedAssetIds.length === this.deviceAssets.length
      if (next.length !== prev.length && this.selectedAssetIds.length > 0) {
        this.saveVariablesDisabled = false
        this.saveCommandsDisabled = false
      }
      if (next.length === 0) {
        this.saveVariablesDisabled = true
        this.saveCommandsDisabled = true
      }
    }
  },
  methods: {
    async getAssets (deviceType) {
      return this.$store.dispatch('getItems', {
        model: {
          name: 'assets',
          sort_col: 'fullPath',
          sort_ord: 'ASC'
        },
        customParams: {
          filter: 'assets.deviceType+' + deviceType,
          limit: 0,
          offset: 0
        },
        saveToStore: false
      })
    },
    tabDisabled (name) {
      const { deviceVariables, deviceCommands } = this
      if (name === 'Variables' && deviceVariables.length === 0) {
        return true
      }
      if (name === 'Commands' && deviceCommands.length === 0) {
        return true
      }
      return false
    },
    getVariableChips (id) {
      const asset = this.deviceAssets.filter((asset) => asset.id === id).shift()
      const chips = []

      // selected variables
      this.selectedVariables.forEach((name) => {
        if (asset.enabledVariables && asset.enabledVariables.includes(name)) {
          chips.push({
            name,
            icon: 'check',
            color: 'gray'
          })
        }
        if ((asset.enabledVariables && asset.enabledVariables.includes(name) === false) || !asset.enabledVariables) {
          chips.push({
            name,
            icon: 'add',
            color: 'green'
          })
        }
      })
      // enabledVariables
      if (asset.enabledVariables) {
        asset.enabledVariables.forEach((name) => {
          if (this.selectedVariables.includes(name) === false) {
            chips.push({
              name,
              icon: 'remove',
              color: 'red'
            })
          }
        })
      }

      return chips
    },
    getCommandChips (id) {
      const asset = this.deviceAssets.filter((asset) => asset.id === id).shift()
      const chips = []

      // selected commands
      this.selectedCommands.forEach((name) => {
        if (asset.enabledCommands && asset.enabledCommands.includes(name)) {
          chips.push({
            name,
            icon: 'check',
            color: 'gray'
          })
        }
        if ((asset.enabledCommands && asset.enabledCommands.includes(name) === false) || !asset.enabledCommands) {
          chips.push({
            name,
            icon: 'add',
            color: 'green'
          })
        }
      })
      // enabledCommands
      if (asset.enabledCommands) {
        asset.enabledCommands.forEach((name) => {
          if (this.selectedCommands.includes(name) === false) {
            chips.push({
              name,
              icon: 'remove',
              color: 'red'
            })
          }
        })
      }

      return chips
    },
    toggleAllVariables () {
      this.selectedVariables = this.allVariables
        ? this.deviceVariables.map((variable) => variable)
        : []
    },
    toggleAllCommands () {
      this.selectedCommands = this.allCommands
        ? this.deviceCommands.map((command) => command)
        : []
    },
    toggleAllAssets () {
      this.selectedAssetIds = this.allAssets
        ? this.deviceAssets.map((asset) => asset.id)
        : []
    },
    async ok () {
      const { selectedVariables, selectedCommands, selectedAssetIds, deviceType, tab } = this
      const modelName = 'assets'

      let promises
      if (tab === 0) {
        const enabledVariables = selectedVariables
        promises = selectedAssetIds.map((id) => {
          return this.$store.dispatch('updateItem', {
            modelName,
            item: {
              id,
              enabledVariables
            }
          })
        })
      }
      if (tab === 1) {
        const enabledCommands = selectedCommands
        promises = selectedAssetIds.map((id) => {
          return this.$store.dispatch('updateItem', {
            modelName,
            item: {
              id,
              enabledCommands
            }
          })
        })
      }

      try {
        this.saveLoading = true
        // do update
        await Promise.all(promises)
        // load updated assets
        this.deviceAssets = await this.getAssets(deviceType)
        // update cached assets
        this.selectedVariables.forEach(assetId => this.$store.commit('clearLogsCache', assetId))
        this.$store.dispatch('refreshStaticItems', { model: { name: 'assets', tree: true } })
        this.$store.commit('showSnackbar', { message: this.$t('saved') })
        if (tab === 0) {
          this.saveVariablesDisabled = true
        }
        if (tab === 1) {
          this.saveCommandsDisabled = true
        }
      } catch ({ message }) {
        this.$store.commit('showSnackbar', { message })
      } finally {
        this.saveLoading = false

        this.$emit('submit', true)
      }
    },
    close () {
      this.$emit('submit', false)
    },
  }
}
</script>
